import React, { useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faSpinner, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import './Announcements.css';
import AnnouncementCard from '../../components/AnnouncementCard/AnnouncementCard';
import { useProfile } from '../../context/ProfileContext';
import announcementService, { MyAnnouncement } from '../../services/announcementService';

const getPriorityConfig = (priority: string, t: (key: string) => string) => {
  const map: Record<string, { color: string; label: string }> = {
    low: { color: '#64748b', label: t('home:announcements.priority.low') },
    normal: { color: '#2563eb', label: t('home:announcements.priority.normal') },
    high: { color: '#ea580c', label: t('home:announcements.priority.high') },
    urgent: { color: '#dc2626', label: t('home:announcements.priority.urgent') },
  };
  return map[priority] || map.normal;
};

const AnnouncementsList: React.FC = () => {
  const { activeProfile } = useProfile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [announcements, setAnnouncements] = useState<MyAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingReadId, setMarkingReadId] = useState<number | null>(null);
  const [initialScrollDone, setInitialScrollDone] = useState(false);

  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const targetId = Number(searchParams.get('id')) || null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    announcementService
      .getAnnouncements()
      .then((data) => {
        if (!cancelled) setAnnouncements(data);
      })
      .catch(() => {
        // silently fail — announcements are non-critical
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeProfile]);

  const setCardRef = (el: HTMLDivElement | null, id: number) => {
    if (el) {
      cardRefs.current.set(id, el);
    } else {
      cardRefs.current.delete(id);
    }
  };

  // Scroll to the target announcement (?id=) and clear the param.
  useEffect(() => {
    if (loading || initialScrollDone) return;
    if (!targetId || !announcements.some((a) => a.id === targetId)) return;

    setInitialScrollDone(true);

    // Let the card mount before scrolling.
    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = cardRefs.current.get(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setSearchParams({}, { replace: true });
      }, 60);
    });
  }, [loading, initialScrollDone, targetId, announcements, setSearchParams]);

  const handleMarkAsRead = async (id: number) => {
    setMarkingReadId(id);
    try {
      await announcementService.markAsRead(id);
      setAnnouncements((prev) =>
        prev.map((a) => (a.id === id ? { ...a, is_read: true } : a))
      );
    } catch (err) {
      console.error('Failed to mark announcement as read:', err);
    } finally {
      setMarkingReadId(null);
    }
  };

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/home');
    }
  };

  return (
    <section className="announcements-page">
      <Container>
        <div className="announcements-page-header">
          <button type="button" className="announcements-back-btn" onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> {t('common:buttons.back')}
          </button>
          <div className="announcements-page-title-wrap">
            <h2 className="announcements-page-title">
              <span className="announcements-page-title-icon">
                <FontAwesomeIcon icon={faBullhorn} />
              </span>
              {t('home:announcements.title')}
            </h2>
            <p className="announcements-page-subtitle">{t('home:announcements.subtitle')}</p>
          </div>
        </div>

        {loading ? (
          <div className="announcements-loading">
            <FontAwesomeIcon icon={faSpinner} spin /> {t('common:labels.loading')}
          </div>
        ) : announcements.length === 0 ? (
          <div className="announcements-empty">
            <FontAwesomeIcon icon={faBullhorn} />
            <span>{t('home:announcements.noAnnouncements')}</span>
          </div>
        ) : (
          <div className="announcements-stack">
            {announcements.map((ann) => {
              const pConfig = getPriorityConfig(ann.priority, t);
              return (
                <AnnouncementCard
                  key={ann.id}
                  ref={(el) => setCardRef(el, ann.id)}
                  layout="row"
                  ann={ann}
                  pConfig={pConfig}
                  expanded={false}
                  onToggleExpand={() => {}}
                  markingReadId={markingReadId}
                  onMarkRead={handleMarkAsRead}
                />
              );
            })}
          </div>
        )}
      </Container>
    </section>
  );
};

export default AnnouncementsList;